// Project-scoped "On Site" panel — embedded as a tab on the client portal's
// project page. Same on-site-now + history + manual sign-in/out as the full
// aggregate "On Site" page, but locked to this one project (no client/site/
// project filters — the project's own site/project id are fixed).
import { useEffect, useState, useCallback } from "react";
import { LogOut, Pencil, AlertTriangle, Mail, Phone, Loader2, RefreshCw, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDT, isToday, daysSince, personTypeDisplay } from "@/lib/onsiteFormat";
import { ManualSignInDialog, SignOutDialog, SignInButton } from "@/components/onsite/OnSiteDialogs";

export default function ProjectOnSitePanel({ clientId, projectId, siteId }) {
    const { user } = useAuth();
    const canEdit = user?.permissions?.inductions === "E";

    const [onSite, setOnSite] = useState([]);
    const [loadingNow, setLoadingNow] = useState(true);
    const [history, setHistory] = useState([]);
    const [loadingHist, setLoadingHist] = useState(true);
    const today = new Date().toISOString().slice(0, 10);
    const weekAgo = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
    const [dateFrom, setDateFrom] = useState(weekAgo);
    const [dateTo, setDateTo] = useState(today);
    const [signInOpen, setSignInOpen] = useState(false);
    const [signOutRec, setSignOutRec] = useState(null);

    const loadOnSite = useCallback(() => {
        setLoadingNow(true);
        const scopeParam = siteId ? `site_id=${siteId}` : `project_id=${projectId}`;
        api.get(`/site-forms/attendance?${scopeParam}&open_only=true`)
            .then((r) => setOnSite(r.data || [])).catch(() => setOnSite([])).finally(() => setLoadingNow(false));
    }, [projectId, siteId]);

    const loadHistory = useCallback(() => {
        setLoadingHist(true);
        const scopeParam = siteId ? `site_id=${siteId}` : `project_id=${projectId}`;
        api.get(`/site-forms/attendance?${scopeParam}&date_from=${dateFrom}&date_to=${dateTo}`)
            .then((r) => setHistory(r.data || [])).catch(() => setHistory([])).finally(() => setLoadingHist(false));
    }, [projectId, siteId, dateFrom, dateTo]);

    useEffect(loadOnSite, [loadOnSite]);
    useEffect(loadHistory, [loadHistory]);

    const reload = () => { loadOnSite(); loadHistory(); };
    const flaggedCount = onSite.filter((r) => !isToday(r.time_in)).length;

    return (
        <div className="space-y-4" data-testid="project-onsite-tab">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-[12.5px] text-[#6F6A64]">Who's signed in on this project right now, and who forgot to sign out.</p>
                <div className="flex items-center gap-2">
                    <button data-testid="project-onsite-refresh" onClick={reload} className="h-8 w-8 grid place-items-center rounded-lg border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }} title="Refresh">
                        <RefreshCw size={13} className="text-[#6F6A64]" />
                    </button>
                    {canEdit && <SignInButton testid="project-onsite-signin-button" onClick={() => setSignInOpen(true)} />}
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <div data-testid="project-onsite-metric-now" className="rounded-[12px] p-4 flex items-center justify-between" style={{ background: "#E8F1E6" }}>
                    <span className="text-[12px] font-semibold text-[#3F7D3A]">On site now</span>
                    <span className="text-2xl font-bold text-[#3F7D3A]">{onSite.length}</span>
                </div>
                <div data-testid="project-onsite-metric-flagged" className="rounded-[12px] p-4 flex items-center justify-between" style={{ background: flaggedCount ? "#FBE8E5" : "#F0EDE8" }}>
                    <span className="text-[12px] font-semibold" style={{ color: flaggedCount ? "#B33A2E" : "#6F6A64" }}>Hasn't signed out (1+ day)</span>
                    <span className="text-2xl font-bold" style={{ color: flaggedCount ? "#B33A2E" : "#6F6A64" }}>{flaggedCount}</span>
                </div>
            </div>

            <div className="bg-white border rounded-[13px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }} data-testid="project-onsite-now-panel">
                <div className="px-5 py-3.5 flex items-center gap-2 border-b" style={{ borderColor: "#F0EDE8" }}>
                    <h3 className="text-[13.5px] font-semibold flex-1">On site now ({onSite.length})</h3>
                    {loadingNow && <Loader2 size={14} className="animate-spin text-[#9A968F]" />}
                </div>
                <div className="p-2">
                    {onSite.length === 0 ? <p className="text-[12px] text-[#9A968F] text-center py-6">Nobody currently signed in on this project.</p> : (
                        <ul className="divide-y" style={{ borderColor: "#F5F3F0" }} data-testid="project-onsite-now-list">
                            {onSite.map((r) => {
                                const flagged = !isToday(r.time_in);
                                return (
                                    <li key={r.id} data-testid={`project-onsite-row-${r.id}`} className="flex items-center gap-3 px-3 py-2.5">
                                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${flagged ? "bg-red-500" : "bg-green-500"}`} />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-[13px] text-[#1F1F1F] truncate">{r.name}</span>
                                                {r.person_type && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#F0EDE8] text-[#6F6A64] flex-shrink-0">{personTypeDisplay(r.person_type, r.person_type_other)}</span>}
                                                {r.company && <span className="text-[11.5px] text-[#9A968F] truncate">· {r.company}</span>}
                                                {flagged && (
                                                    <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">
                                                        <AlertTriangle size={10} /> {daysSince(r.time_in)}d — hasn't signed out
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-[#6F6A64] flex items-center gap-2.5 mt-0.5 flex-wrap">
                                                <span>Signed in {fmtDT(r.time_in)}</span>
                                                {r.site_name && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {r.site_name}</span>}
                                                {r.email && <span className="inline-flex items-center gap-1"><Mail size={10} /> {r.email}</span>}
                                                {r.mobile && <span className="inline-flex items-center gap-1"><Phone size={10} /> {r.mobile}</span>}
                                                {r.manual_entry && <span className="italic">Manually signed in by {r.signed_in_by_name || "staff"}</span>}
                                            </div>
                                        </div>
                                        {canEdit && (
                                            <button data-testid={`project-onsite-signout-${r.id}`} onClick={() => setSignOutRec({ id: r.id, name: r.name, time_out: null })}
                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[7px] text-[11.5px] font-semibold border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>
                                                <LogOut size={12} /> Sign out
                                            </button>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>

            <div className="bg-white border rounded-[13px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }} data-testid="project-onsite-history-panel">
                <div className="px-5 py-3.5 flex items-center gap-2.5 border-b flex-wrap" style={{ borderColor: "#F0EDE8" }}>
                    <h3 className="text-[13.5px] font-semibold">History</h3>
                    <div className="flex items-center gap-1.5 ml-auto text-[12px]">
                        <input type="date" data-testid="project-onsite-history-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 px-2 rounded-md border text-[12px]" style={{ borderColor: "#E5E0DA" }} />
                        <span className="text-[#9A968F]">to</span>
                        <input type="date" data-testid="project-onsite-history-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 px-2 rounded-md border text-[12px]" style={{ borderColor: "#E5E0DA" }} />
                    </div>
                    {loadingHist && <Loader2 size={14} className="animate-spin text-[#9A968F]" />}
                </div>
                <div className="overflow-x-auto">
                    {history.length === 0 ? <p className="text-[12px] text-[#9A968F] text-center py-6">No attendance recorded in this range.</p> : (
                        <table className="w-full text-[12.3px]" data-testid="project-onsite-history-table">
                            <thead>
                                <tr className="text-left text-[10.5px] uppercase tracking-wide text-[#9A968F] border-b" style={{ borderColor: "#F0EDE8" }}>
                                    <th className="px-4 py-2">Name</th><th className="px-4 py-2">Person type</th><th className="px-4 py-2">Company</th>
                                    <th className="px-4 py-2">Site</th><th className="px-4 py-2">Signed in</th>
                                    <th className="px-4 py-2">Signed out</th><th className="px-4 py-2" />
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((r) => (
                                    <tr key={r.id} data-testid={`project-onsite-history-row-${r.id}`} className="border-b last:border-b-0" style={{ borderColor: "#F5F3F0" }}>
                                        <td className="px-4 py-2 font-medium text-[#1F1F1F]">{r.name}</td>
                                        <td className="px-4 py-2 text-[#6F6A64]">{r.person_type ? personTypeDisplay(r.person_type, r.person_type_other) : "—"}</td>
                                        <td className="px-4 py-2 text-[#6F6A64]">{r.company || "—"}</td>
                                        <td className="px-4 py-2 text-[#6F6A64]">{r.site_name || "—"}</td>
                                        <td className="px-4 py-2 text-[#6F6A64]">{fmtDT(r.time_in)}</td>
                                        <td className="px-4 py-2">
                                            {r.time_out ? (
                                                <span className="text-[#6F6A64]">{fmtDT(r.time_out)}{r.edited_at && <span className="italic text-[10px] ml-1">(corrected)</span>}</span>
                                            ) : <span className="text-green-600 font-semibold text-[11px]">On site</span>}
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            {canEdit && (
                                                <button data-testid={`project-onsite-history-edit-${r.id}`} onClick={() => setSignOutRec({ id: r.id, name: r.name, time_out: r.time_out })}
                                                    className="text-[#9A968F] hover:text-[#1F1F1F]" title={r.time_out ? "Correct sign-out time" : "Sign out"}>
                                                    <Pencil size={13} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {signInOpen && (
                <ManualSignInDialog clientId={clientId} defaultSiteId={siteId} defaultProjectId={projectId} hideLocationFields
                    onClose={() => setSignInOpen(false)} onDone={() => { setSignInOpen(false); reload(); }} />
            )}
            {signOutRec && (
                <SignOutDialog rec={signOutRec} onClose={() => setSignOutRec(null)} onDone={() => { setSignOutRec(null); reload(); }} />
            )}
        </div>
    );
}
